import React, { createRef, useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Tabs,
  Tab,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Tooltip,
  Pagination,
  PaginationItem,
} from "@mui/material";
import { format } from "date-fns";
import LoadingSpinner from "../../components/LoadingSpinner";
import ReactModal from "react-modal";
import ChangeStatusCampaignModal from "../../pages/campaign/leads/ChangeStatusCampaignModal";
import { useCampaignChangeStatusQuery } from "../../Services/campign/campignList";
import { maskPhoneNumber } from "../reusableComponent/MaskedFields";
import {
  getIconClasses,
  getBadgeClasses,
  getPriorityFlagColor,
} from "../../components/reusableComponent/IconAndBadgeClasses";
import { capitalizeFirstLetter } from "../../components/reusableComponent/CapitalLetter";
import "../../pages/ticket/TicketListView.scss";

interface IProps {
  listOfLeads: any;
  taskRes: any;
  CampaignWorkflow: any;
  setStoreKey: any;
  setValue: any;
  value: any;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  useData: any;
  campign: any;
  setSelectedTickets: any;
  selectedTickets: any;
  setLiftUiData: any;
  refetch: any;
  selectedWorkFlow: any;
  selectCampaign: any;
  selectedOptions: any;
  checkTenant: any;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
}

const customModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "450px",
  },
};

const CampaignListCardView: React.FC<IProps> = ({
  listOfLeads,
  taskRes,
  CampaignWorkflow,
  setStoreKey,
  setValue,
  value,
  currentPage,
  setCurrentPage,
  campign,
  setSelectedTickets,
  selectedTickets,
  setLiftUiData,
  refetch,
  selectedWorkFlow,
  selectCampaign,
  selectedOptions,
  checkTenant,
  pageSize,
  setPageSize,
}) => {
  const taskResActionMenuRef = useRef<any[]>([]);
  taskResActionMenuRef.current = taskRes.map(
    (item: any, i: number) => taskResActionMenuRef.current[i] ?? createRef(),
  );

  // ── refs (mirrors TicketListView) ─────────────────────────────────────────
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRestoreCleanupRef = useRef<(() => void) | null>(null);

  // ── state ─────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState<boolean>(false);
  const [selectAll, setSelectAll] = useState(false);
  const [uiData, setUiData] = useState<any[]>([]);
  const [keyState, setKeyState] = useState<any[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<any>("");
  const [storingTicketId, setStoringTicketId] = useState("");
  const [originalStatus, setOriginalStatus] = useState("");
  const [isStatusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedAllStatus, setAllSelectedStatus] = useState<any>("");
  const [selectedCampaign, setSelectedCampaign] = useState<any>([]);

  const pageSizeOptions = [5, 10, 25, 50, 100];
  const leadCount = campign?.count ?? 0;
  const totalPages = Math.ceil(leadCount / pageSize);

  const checkUserRole = localStorage.getItem("user_role");
  const tenant = localStorage.getItem("circleone-user");
  const tenantName = window.location.hostname.split(".")[0]?.toLowerCase();

  const { data: campaignData, refetch: refetchAuditTrail } =
    useCampaignChangeStatusQuery(selectCampaign);

  // ── helpers ───────────────────────────────────────────────────────────────
  const truncateDescription = (description: any, maxLength: number) => {
    if (description?.length > maxLength)
      return description.slice(0, maxLength) + "...";
    return description;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return format(date, "dd-MM-yyyy HH:mm:ss");
  };

  const getStatusStyle = (status_type: string) => {
    switch (status_type) {
      case "active":
        return "status-open";
      case "closed":
        return "status-closed";
      case "reopen":
        return "status-reopen";
      case "wip":
        return "status-wip";
      default:
        return "status-default";
    }
  };

  const getPriorityStyle = (isHighPriority: boolean) =>
    isHighPriority ? "priority-high" : "priority-low";

  // ── scroll position (mirrors TicketListView) ──────────────────────────────
  const storeScrollPosition = (leadId: string) => {
    const el = scrollContainerRef.current;
    if (el) {
      sessionStorage.setItem(
        `campaignListCardScrollPosition_${value}`,
        String(el.scrollTop),
      );
    }
    sessionStorage.setItem(`lastViewedLeadCard_${value}`, leadId);
  };

  const restoreScrollPosition = () => {
    const saved = sessionStorage.getItem(
      `campaignListCardScrollPosition_${value}`,
    );
    if (!saved) return () => {};
    const targetScrollTop = parseInt(saved, 10);
    if (Number.isNaN(targetScrollTop) || targetScrollTop < 0) return () => {};

    const applyScroll = () => {
      const el = scrollContainerRef.current;
      if (el) el.scrollTop = targetScrollTop;
    };

    const delays = [0, 50, 150, 300, 500, 800, 1200, 1800, 2500, 3500];
    const timeouts = delays.map((d) => setTimeout(applyScroll, d));
    requestAnimationFrame(() => requestAnimationFrame(applyScroll));
    return () => timeouts.forEach((t) => clearTimeout(t));
  };

  const clearScrollPosition = () => {
    sessionStorage.removeItem(`campaignListCardScrollPosition_${value}`);
    sessionStorage.removeItem(`lastViewedLeadCard_${value}`);
  };

  // ── workflow tab processing ────────────────────────────────────────────────
  const processData = async () => {
    const uiFormattedData: any[] = [];
    const keysForState: any[] = [];

    if (CampaignWorkflow && typeof CampaignWorkflow === "object") {
      await Object.entries(CampaignWorkflow).forEach(([key, val]) => {
        if (typeof val === "object" && val !== null && !Array.isArray(val)) {
          Object.entries(val).forEach(([nestedKey, nestedValue]) => {
            uiFormattedData.push({ key: nestedKey, value: nestedValue });
            keysForState.push(key);
          });
        } else if (Array.isArray(val)) {
          val.forEach((item) => {
            if (item && typeof item === "object") {
              Object.entries(item).forEach(([nestedKey, nestedValue]) => {
                uiFormattedData.push({ key: nestedKey, value: nestedValue });
                keysForState.push(key);
              });
            }
          });
        }
      });
      setUiData(uiFormattedData);
      setKeyState(keysForState);
    }
  };

  useEffect(() => {
    processData();
  }, [CampaignWorkflow]);

  // filter reopen for specific tenants
  const filteredUiData = uiData.filter((item) => {
    if (
      (tenantName === "meta" || tenantName === "payswiffibl") &&
      item.key.toLowerCase() === "reopen"
    )
      return false;
    return true;
  });

  // ── loading ───────────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    setTimeout(() => setLoading(false), 3000);
  }, [currentPage]);

  // ── restore scroll after load ─────────────────────────────────────────────
  useEffect(() => {
    const canRestore =
      !loading && campign?.results && campign.results.length > 0;
    if (canRestore) {
      const timer = setTimeout(() => {
        const cleanup = restoreScrollPosition();
        if (typeof cleanup === "function") {
          scrollRestoreCleanupRef.current = cleanup;
        }
      }, 50);
      return () => {
        clearTimeout(timer);
        if (scrollRestoreCleanupRef.current) {
          scrollRestoreCleanupRef.current();
          scrollRestoreCleanupRef.current = null;
        }
      };
    }
  }, [loading, campign?.results, value]);

  // ── store scroll on page unload ───────────────────────────────────────────
  useEffect(() => {
    const handleBeforeUnload = () => {
      const el = scrollContainerRef.current;
      if (el) {
        sessionStorage.setItem(
          `campaignListCardScrollPosition_${value}`,
          String(el.scrollTop),
        );
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [value]);

  // ── liftUiData ────────────────────────────────────────────────────────────
  useEffect(() => {
    setLiftUiData(uiData);
  }, [uiData]);

  // ── select all ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!campign?.results?.length) {
      setSelectAll(false);
    } else if (selectedTickets.length === campign?.results?.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedTickets]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectAll(event.target.checked);
    if (event.target.checked && campign?.results?.length > 0) {
      setSelectedTickets(campign?.results.map((r: any) => r.id));
    } else {
      setSelectedTickets([]);
    }
  };

  const handleCampignSelect = (leadId: string) => {
    setSelectedTickets((prev: any[]) => {
      const isSelected = prev.includes(leadId);
      return isSelected
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId];
    });
  };

  // ── pagination ────────────────────────────────────────────────────────────
  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    clearScrollPosition();
  };

  const handlePageSizeChange = (event: any) => {
    const newSize = parseInt(event.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    clearScrollPosition();
    sessionStorage.setItem("campaignListCardViewPageSize", newSize.toString());
  };

  // ── status ────────────────────────────────────────────────────────────────
  const handleStatusChangeeee = (campignItem: any) => {
    setStoringTicketId(campignItem?.id);
    setSelectedCampaign(campignItem);
  };

  const openStatusModal = () => {
    setOriginalStatus(selectedStatus);
    setStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    setSelectedStatus(originalStatus);
    setStatusModalOpen(false);
  };

  const handlerStatus = (allStatus: any) => {
    setAllSelectedStatus(allStatus);
    setSelectedStatus(allStatus.status_type);
    openStatusModal();
  };

  const formatStatusData = (status: any) => {
    if (status.status_type === "active" || status.status_type === "reopen") {
      return {
        status_type: status.status_type,
        status: status.status,
      };
    } else {
      return {
        id: status.status?.id,
        status_type: status.status_type,
        status: status.status?.status,
      };
    }
  };

  const statusOptions = campaignData?.results?.[0]?.all_statuses?.map(
    (status: any) => formatStatusData(status),
  );

  useEffect(() => {
    const defaultSelections = campign?.results?.reduce(
      (acc: any, status: any) => {
        acc[status?.id] = status?.status_display || "";
        return acc;
      },
      {},
    );
    setSelectedStatus(defaultSelections || {});
  }, [campign]);

  const handleStatusUpdate = () => {
    setStatusModalOpen(false);
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: { maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP, width: 120 },
    },
  };

  const handleLeadClick = (leadId: string) => {
    storeScrollPosition(leadId);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* ── Tabs header ── */}
          <div className="tabs-header-section">
            <div className="tabs-header-content mx-5">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="select-all-checkbox"
              />
              <Tabs
                value={filteredUiData.findIndex((item) => item.key === value)}
                onChange={(_, newValue) => {
                  clearScrollPosition();
                  setStoreKey(keyState[newValue]);
                  setValue(filteredUiData[newValue].key);
                  setCurrentPage(1);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  flex: 1,
                  minHeight: "44px",
                  backgroundColor: "#ffffff",
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#6366f1",
                    height: "2px",
                  },
                  "& .MuiTab-root": {
                    textTransform: "none",
                    minHeight: "44px",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#6c757d",
                    padding: "10px 20px",
                    transition: "all 0.2s ease",
                    minWidth: "auto",
                    "&:hover": { color: "#495057", backgroundColor: "#f8f9fa" },
                    "&.Mui-selected": { color: "#6366f1", fontWeight: 600 },
                  },
                  "& .MuiTabs-scrollButtons": {
                    color: "#6c757d",
                    "&.Mui-disabled": { opacity: 0.3 },
                  },
                }}
              >
                {filteredUiData.map((info, index) => (
                  <Tooltip
                    key={index}
                    title={`${info.key} (${info.value})`}
                    arrow
                  >
                    <Tab
                      label={truncateDescription(
                        `${info.key} (${info.value})`,
                        25,
                      )}
                    />
                  </Tooltip>
                ))}
              </Tabs>
            </div>
          </div>

          {/* ── Scrollable container ── */}
          <div
            ref={scrollContainerRef}
            className="ticket-list-scroll-container"
          >
            {!campign?.results || campign?.results?.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-inbox empty-icon"></i>
                <h5 className="empty-title">No leads available</h5>
                <p className="empty-description">
                  No leads found for the current selection. Try adjusting your
                  filters or campaign selection.
                </p>
              </div>
            ) : (
              <div className="modern-tickets-container">
                {campign?.results?.map((lead: any) => (
                  <div key={lead?.id} className="modern-ticket-card">
                    {/* Checkbox */}
                    <div className="card-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedTickets.includes(lead?.id)}
                        onChange={() => handleCampignSelect(lead?.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>

                    {/* ── Card Header ── */}
                    <div className="card-header-section">
                      <div className="header-left">
                        <span className="ticket-ref-id">
                         {lead?.id}
                        </span>
                        <div
                          className={`channel-badge-wrapper ${getBadgeClasses(
                            lead?.channel_name,
                          )}`}
                          title={lead?.channel_name}
                        >
                          <i className={getIconClasses(lead?.channel_name)}></i>
                        </div>
                        <span className="channel-label">
                          {capitalizeFirstLetter(lead?.channel_name || "")}
                        </span>
                        {lead?.attachments?.length > 0 && (
                          <Tooltip
                            title={`${lead.attachments.length} attachment(s)`}
                          >
                            <i className="bi bi-paperclip attachment-icon"></i>
                          </Tooltip>
                        )}
                        {checkTenant === "meta" && (
                          <Tooltip
                            title={
                              lead.broadcast_msg === true
                                ? "Whatsapp broadcast sent successfully"
                                : lead.broadcast_msg === "false"
                                ? "Message Sending Failed"
                                : "Message Not Sent"
                            }
                          >
                            <i
                              className={`bi ${
                                lead.broadcast_msg === true
                                  ? "bi-check-circle-fill text-success"
                                  : lead.broadcast_msg === "false"
                                  ? "bi-x-circle-fill text-danger"
                                  : "bi-dash-circle-fill text-muted"
                              }`}
                              style={{ fontSize: "1rem" }}
                            ></i>
                          </Tooltip>
                        )}
                      </div>

                      <div className="header-right">
                        <span
                          className={`status-pill ${getStatusStyle(
                            lead?.status_type,
                          )}`}
                        >
                          {lead?.status_display?.toUpperCase() || "OPEN"}
                        </span>
                        <span
                          className={`priority-pill ${getPriorityStyle(
                            lead?.is_high_priority,
                          )}`}
                        >
                          {lead?.is_high_priority ? "HIGH" : "LOW"}
                        </span>
                        <Tooltip
                          title={`${
                            lead?.is_high_priority ? "High" : "Low"
                          } Priority`}
                        >
                          <i
                            className="bi bi-flag-fill priority-flag"
                            style={{
                              color: getPriorityFlagColor(
                                lead?.is_high_priority ? "high" : "low",
                              ),
                            }}
                          ></i>
                        </Tooltip>
                      </div>
                    </div>

                    {/* ── Card Body ── */}
                    <Link
                      to={`/campaign/lead/${lead?.id}`}
                      onClick={() => handleLeadClick(lead?.id)}
                      className="card-body-link"
                    >
                      <div className="card-body-section">
                        <h3 className="ticket-main-title">
                          {[lead?.first_name, lead?.last_name]
                            .filter(Boolean)
                            .map(capitalizeFirstLetter)
                            .join(" ") ||
                            capitalizeFirstLetter(lead?.account_name) ||
                            "—"}
                        </h3>
                       
                      </div>
                    </Link>

                    {/* ── Card Footer ── */}
                    <div className="card-footer-section">
                      <div className="footer-info-grid">
                        {/* 1. Assignee */}
                        <div className="footer-item">
                          <span className="footer-label">Assignee</span>
                          <div className="footer-value-wrapper">
                            <div className="avatar-circle">
                              {lead?.assignee?.username
                                ?.charAt(0)
                                ?.toUpperCase() || "U"}
                            </div>
                            <span className="footer-value">
                              {capitalizeFirstLetter(
                                lead?.assignee?.username?.split("@")[0] ??
                                  "Unassigned",
                              )}
                            </span>
                          </div>
                        </div>

                        {/* 2. Account name */}
                        <div className="footer-item">
                          <span className="footer-label">Account name</span>
                          <span className="footer-value">
                            {capitalizeFirstLetter(lead?.account_name) || "—"}
                          </span>
                        </div>

                        {/* 3. Created by */}
                        <div className="footer-item">
                          <span className="footer-label">Created by</span>
                          <span className="footer-value">
                            {lead?.created_by?.username || "—"}
                          </span>
                        </div>

                        {/* 4. Workflow */}
                        <div className="footer-item">
                          <span className="footer-label">Workflow</span>
                          <span className="footer-value">
                            {lead?.campaign?.campaign_name?.name || "—"}
                          </span>
                        </div>

                        {/* 5. Current team */}
                        <div className="footer-item">
                          <span className="footer-label">Current team</span>
                          <span className="footer-value">
                            {lead?.campaign?.team?.name || "—"}
                          </span>
                        </div>

                        {/* 6. Mobile no. */}
                        <div className="footer-item">
                          <span className="footer-label">Mobile no.</span>
                          <span className="footer-value">
                            {checkUserRole === "agent" && tenant === "briezi"
                              ? maskPhoneNumber(lead?.phone_number)
                              : lead?.phone_number || "—"}
                          </span>
                        </div>

                        {/* 7. Created at */}
                        <div className="footer-item">
                          <span className="footer-label">Created at</span>
                          <span className="footer-value">
                            {formatDate(lead?.created)}
                          </span>
                        </div>
                      </div>

                      {/* ── Footer actions ── */}
                      <div className="footer-actions">
                        {/* View Lead */}
                        {/* <Link
                          to={`/campaign/lead/${lead?.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLeadClick(lead?.id);
                          }}
                        >
                          <Tooltip title="View Lead">
                            <button className="action-icon-btn">
                              <i className="bi bi-eye action-icon"></i>
                            </button>
                          </Tooltip>
                        </Link> */}

                        {/* Change Status */}
                        <div
                          className="status-change-wrapper"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FormControl
                            className="status-select-control"
                            size="small"
                          >
                            <InputLabel id={`status-select-label-${lead.id}`}>
                              Change Status
                            </InputLabel>
                            <Select
                              labelId={`status-select-label-${lead.id}`}
                              id={`status-select-${lead.id}`}
                              value={selectedStatus[lead.id] || ""}
                              onChange={() => handleStatusChangeeee(lead)}
                              label="Change Status"
                              MenuProps={MenuProps}
                            >
                              {statusOptions?.map((status: any) => (
                                <MenuItem
                                  key={status?.id}
                                  value={status?.status}
                                  onClick={() => handlerStatus(status)}
                                  title={status?.status}
                                >
                                  {truncateDescription(status?.status, 25)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Pagination footer ── */}
          <div className="email-pagination">
            <div className="email-pagination__left">
              <span className="email-pagination__label">Show</span>
              <select
                className="email-pagination__select"
                value={pageSize}
                onChange={handlePageSizeChange}
                aria-label="Select page size"
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className="email-pagination__label">per page</span>
            </div>

            <div className="email-pagination__right">
              {totalPages > 0 && (
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                  size="small"
                  renderItem={(item) => <PaginationItem {...item} />}
                />
              )}
            </div>
          </div>
        </>
      )}

      {/* Status Change Modal — rendered outside scroll container to avoid z-index issues */}
      <ReactModal
        isOpen={isStatusModalOpen}
        style={customModalStyles}
        contentLabel="Change Status"
        shouldCloseOnOverlayClick={false}
      >
        <ChangeStatusCampaignModal
          creatingNewContactDetails={""}
          data={campaignData}
          closeStatusModal={closeStatusModal}
          refetch={refetch}
          refetchAuditTrail={refetchAuditTrail}
          selectedStatus={selectedStatus}
          handleStatusUpdate={handleStatusUpdate}
          selectedAllStatus={selectedAllStatus}
          storingTicketId={storingTicketId}
          refetchAuditTrailData={undefined}
          campign={selectedCampaign}
        />
      </ReactModal>
    </>
  );
};

export default CampaignListCardView;
